import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Calendar, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

import { planningAPI } from "../services/planningService";
import { employeesAPI, pointagesAPI } from "../services/api";
import AttendanceFiltersCard from "./attendance/AttendanceFiltersCard";
import AttendanceLegendCard from "./attendance/AttendanceLegendCard";
import AttendanceDayGroup from "./attendance/AttendanceDayGroup";
import AttendanceDetailDialog from "./attendance/AttendanceDetailDialog";

const AttendanceViewWithColors = () => {
  const { t, i18n } = useTranslation();
  const [attendances, setAttendances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ employee_id: "all", start_date: "", end_date: "" });
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [calculationDetail, setCalculationDetail] = useState(null);

  useEffect(() => { loadEmployees(); setDefaultDates(); }, []);

  useEffect(() => {
    if (filters.start_date && filters.end_date) loadAttendances();
  }, [filters]);

  const loadEmployees = async () => {
    try {
      const data = await employeesAPI.getEmployees({ limit: 1000 });
      setEmployees(data.employees || []);
    } catch (error) {
    }
  };

  const loadAttendances = async () => {
    try {
      setLoading(true);
      const logsFilters = { start_date: filters.start_date, end_date: filters.end_date, skip: 0, limit: 1000 };

      if (filters.employee_id && filters.employee_id !== "all") {
        const selectedEmployee = employees.find((emp) => emp.id === parseInt(filters.employee_id));
        if (selectedEmployee) {
          let userIdFilter = null;
          if (selectedEmployee.badge_number) {
            const badgeMatch = selectedEmployee.badge_number.match(/(\d+)$/);
            if (badgeMatch) userIdFilter = parseInt(badgeMatch[1]).toString();
          }
          if (!userIdFilter) userIdFilter = selectedEmployee.id.toString();
          logsFilters.user_id = userIdFilter;
        } else {
          toast.warning(t("attColors.empNotFound"));
          setAttendances([]);
          return;
        }
      }

      const response = await pointagesAPI.getAttendanceLogs(logsFilters);
      setAttendances(response.data?.raw || []);
    } catch (error) {
      toast.error(t("attColors.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (attendance) => {
    try {
      setSelectedAttendance(attendance);
      const detail = await planningAPI.calculations.calculateAttendanceForDate(
        attendance.employee_id,
        attendance.date,
        attendance.actual_start_time,
        attendance.actual_end_time
      );
      setCalculationDetail(detail);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error(t("attColors.cantCalc"));
    }
  };

  const setDefaultDates = () => {
    const today = new Date();
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    setFilters((prev) => ({
      ...prev,
      start_date: lastWeek.toISOString().split("T")[0],
      end_date: today.toISOString().split("T")[0],
    }));
  };

  const groupAttendancesByDate = () => {
    const grouped = {};
    attendances.forEach((attendance) => {
      const timestamp = new Date(attendance.timestamp);
      const dateKey = timestamp.toLocaleDateString(i18n.language);
      if (!grouped[dateKey]) grouped[dateKey] = { date: timestamp, dateKey, logs: [] };
      grouped[dateKey].logs.push(attendance);
    });
    return Object.values(grouped)
      .sort((a, b) => b.date - a.date)
      .map((group) => ({ ...group, logs: group.logs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)) }));
  };

  const groupedAttendances = groupAttendancesByDate();

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{t("attColors.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("attColors.subtitle")}</p>
        </div>
        <Button onClick={loadAttendances} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" /> {t("common.refresh")}
        </Button>
      </div>

      <AttendanceFiltersCard
        filters={filters}
        setFilters={setFilters}
        employees={employees}
        setDefaultDates={setDefaultDates}
      />

      <AttendanceLegendCard />

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-muted-foreground">{t("attColors.loading")}</p>
          </CardContent>
        </Card>
      ) : attendances.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>{t("attColors.noPunch")}</p>
            <p className="text-sm mt-1">{t("attColors.selectPeriod")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groupedAttendances.map((group) => (
            <AttendanceDayGroup key={group.dateKey} group={group} employees={employees} />
          ))}
          <Card>
            <CardContent className="py-4">
              <div className="text-sm text-muted-foreground text-center">
                {t("attColors.totalSummary", { punches: attendances.length, days: groupedAttendances.length })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <AttendanceDetailDialog
        open={showDetailDialog}
        onOpenChange={setShowDetailDialog}
        selectedAttendance={selectedAttendance}
        calculationDetail={calculationDetail}
        employees={employees}
      />
    </div>
  );
};

export default AttendanceViewWithColors;
